// ─────────────────────────────────────────────────────────
// Voice command interpreter for Smart Entry V2.
//
// Splits spoken text into ONE of:
//   - field-targeted command: "التاريخ ٨ مارس ٢٠٢٦" / "payee John" / "line 2 …"
//   - free dictation:         "خليل قاسم مصاريف سيارات ٥٠"
//
// In field-targeted mode we patch ONLY the targeted slot (without disturbing
// the rest of the voucher). In free mode we hand the whole text to the
// existing /api/SmartEntry/parse pipeline.
// ─────────────────────────────────────────────────────────

export type FieldTarget =
    | { kind: 'date'; rawValue: string }
    | { kind: 'voucherRef'; rawValue: string }
    | { kind: 'payee'; rawValue: string }
    | { kind: 'notes'; rawValue: string }
    | { kind: 'line'; lineNumber: number; rawValue: string }
    | { kind: 'description'; lineNumber?: number; rawValue: string }
    | { kind: 'amount'; lineNumber?: number; rawValue: string }
    | { kind: 'account'; lineNumber?: number; rawValue: string }
    | { kind: 'pickAlternative'; lineNumber?: number; hint: string }
    | { kind: 'deleteLine'; lineNumber: number }
    // Mode switching — no value, just activate a field-targeting mode
    | { kind: 'switchMode'; target: 'date' | 'voucher' | 'payee' | 'notes' | 'account' | 'amount' }
    | { kind: 'nextLine' }
    | { kind: 'previousLine' }
    | { kind: 'newLine' }
    | { kind: 'save' }
    | { kind: 'clear' }
    | { kind: 'free'; rawValue: string };

// Normalize Arabic — mirror of backend NormalizeArabic (alef/ya/ta-marbuta).
export function normalizeAr(s: string): string {
    if (!s) return '';
    return s
        .replace(/[ً-ٰٟ]/g, '')   // diacritics
        .replace(/[أإآٱ]/g, 'ا')
        .replace(/[ىئ]/g, 'ي')
        .replace(/ة/g, 'ه')
        .replace(/ؤ/g, 'و')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

// Convert Arabic-Indic digits to ASCII.
export function arabicToLatinDigits(s: string): string {
    return s.replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
}

// Map common Arabic spoken numerals to digits for "سطر اثنين" → "سطر 2".
const SPOKEN_NUMERALS: Record<string, number> = {
    'صفر': 0,
    'واحد': 1, 'الاول': 1, 'الأول': 1, 'اول': 1, 'first': 1, 'one': 1,
    'اثنين': 2, 'اثنان': 2, 'الثاني': 2, 'ثاني': 2, 'الثانيه': 2, 'second': 2, 'two': 2,
    'ثلاثه': 3, 'ثلاثة': 3, 'الثالث': 3, 'ثالث': 3, 'third': 3, 'three': 3,
    'اربعه': 4, 'اربعة': 4, 'الرابع': 4, 'رابع': 4, 'fourth': 4, 'four': 4,
    'خمسه': 5, 'خمسة': 5, 'الخامس': 5, 'خامس': 5, 'fifth': 5, 'five': 5,
    'سته': 6, 'ستة': 6, 'السادس': 6, 'سادس': 6, 'sixth': 6, 'six': 6,
    'سبعه': 7, 'سبعة': 7, 'السابع': 7, 'سابع': 7, 'seventh': 7, 'seven': 7,
    'ثمانيه': 8, 'ثمانية': 8, 'الثامن': 8, 'ثامن': 8, 'eighth': 8, 'eight': 8,
    'تسعه': 9, 'تسعة': 9, 'التاسع': 9, 'تاسع': 9, 'ninth': 9, 'nine': 9,
    'عشره': 10, 'عشرة': 10, 'العاشر': 10, 'عاشر': 10, 'tenth': 10, 'ten': 10,
};

function parseSpokenNumber(token: string): number | null {
    const t = normalizeAr(token);
    if (/^\d+$/.test(t)) return parseInt(t, 10);
    if (SPOKEN_NUMERALS[t] !== undefined) return SPOKEN_NUMERALS[t];
    return null;
}

// Field keyword groups. Each entry: { match: RegExp, kind: target kind, …captures }
//
// Order matters: more specific patterns (e.g., "وصف السطر 2") must come BEFORE
// generic ones ("الوصف"). We test top-to-bottom and stop at the first match.
const KEYWORD_PATTERNS: Array<{
    regex: RegExp;
    build: (m: RegExpMatchArray) => FieldTarget | null;
}> = [
    // ── Save / Clear voice commands (no value) ──
    // Save: trigger the dialog's Save button without touching the form.
    {
        regex: /^(?:احفظ|حفظ|سيف|اعتمد|اعتماد|save|save\s*it|store)\s*$/i,
        build: () => ({ kind: 'save' } as any),
    },
    // Clear: wipe the live transcript so the next utterance starts clean.
    {
        regex: /^(?:امسح|مسح|نظف|نظّف|الغ[ىي]|الغاء|كلير|clear|reset|clean)\s*$/i,
        build: () => ({ kind: 'clear' } as any),
    },

    // ── Mode switch (no value — just activate the target field) ──
    // "حدد X" is the preferred trigger; "اختر X" / "ضع X" / English variants
    // remain supported so older muscle memory still works.
    {
        regex: /^(?:حدد|اختر|ضع|اذهب\s*الى|اذهب\s*إلى|الى|إلى|select|choose|switch\s*to|go\s*to|mode)\s+(التاريخ|تاريخ|date|رقم\s*السند|السند|voucher|reference|ref|المستفيد|مستفيد|payee|ملاحظات|ملاحظة|notes|الحساب|حساب|account|المبلغ|مبلغ|amount)\s*$/i,
        build: (m) => {
            const t = normalizeAr(m[1]);
            let target: 'date' | 'voucher' | 'payee' | 'notes' | 'account' | 'amount' | null = null;
            if (/^(date|التاريخ|تاريخ)$/i.test(t)) target = 'date';
            else if (/(voucher|reference|ref|السند|رقم)/i.test(t)) target = 'voucher';
            else if (/^(payee|المستفيد|مستفيد)$/i.test(t)) target = 'payee';
            else if (/^(notes|ملاحظات|ملاحظه)$/i.test(t)) target = 'notes';
            else if (/^(account|الحساب|حساب)$/i.test(t)) target = 'account';
            else if (/^(amount|المبلغ|مبلغ)$/i.test(t)) target = 'amount';
            return target ? { kind: 'switchMode', target } : null;
        },
    },

    // ── Next / previous / new line navigation ──
    {
        regex: /^(?:السطر\s*التالي|التالي|next\s*line|next)\s*$/i,
        build: () => ({ kind: 'nextLine' }),
    },
    {
        regex: /^(?:السطر\s*السابق|السابق|previous\s*line|previous|prev)\s*$/i,
        build: () => ({ kind: 'previousLine' }),
    },
    {
        regex: /^(?:سطر\s*جديد|اضف\s*سطر|اضف\s*سطر\s*جديد|new\s*line|add\s*line|add\s*new\s*line)\s*$/i,
        build: () => ({ kind: 'newLine' }),
    },

    // ── Delete line ──
    {
        regex: /^(?:احذف|الغ[ىي]|delete|remove)\s+(?:السطر|سطر|line|row)\s+(\S+)\s*$/i,
        build: (m) => {
            const n = parseSpokenNumber(m[1]);
            return n != null ? { kind: 'deleteLine', lineNumber: n } : null;
        },
    },

    // ── Pick alternative ──  "من النتائج الإدارة" / "اختر الإدارة"
    {
        regex: /^(?:اختر|اخت[رير]|pick|select|من\s+النتائج|from\s+results)\s+(.+)$/i,
        build: (m) => ({ kind: 'pickAlternative', hint: m[1].trim() }),
    },

    // ── Description for a specific line: "وصف السطر 2 ..." / "description line 2 ..."
    {
        regex: /^(?:الشرح|شرح|الوصف|وصف|البيان|بيان|description|desc|note)\s+(?:السطر|سطر|line|row)\s+(\S+)\s+(.+)$/i,
        build: (m) => {
            const n = parseSpokenNumber(m[1]);
            return n != null ? { kind: 'description', lineNumber: n, rawValue: m[2].trim() } : null;
        },
    },

    // ── Plain description (current active line) ──
    {
        regex: /^(?:الشرح|شرح|الوصف|وصف|البيان|بيان|description|desc|note)\s+(.+)$/i,
        build: (m) => ({ kind: 'description', rawValue: m[1].trim() }),
    },

    // ── Amount for a line: "مبلغ السطر 2 ٤٠" ──
    {
        regex: /^(?:المبلغ|مبلغ|amount)\s+(?:السطر|سطر|line|row)\s+(\S+)\s+(.+)$/i,
        build: (m) => {
            const n = parseSpokenNumber(m[1]);
            return n != null ? { kind: 'amount', lineNumber: n, rawValue: m[2].trim() } : null;
        },
    },
    {
        regex: /^(?:المبلغ|مبلغ|amount)\s+(.+)$/i,
        build: (m) => ({ kind: 'amount', rawValue: m[1].trim() }),
    },

    // ── Account for a line ──
    {
        regex: /^(?:الحساب|حساب|account)\s+(?:السطر|سطر|line|row)\s+(\S+)\s+(.+)$/i,
        build: (m) => {
            const n = parseSpokenNumber(m[1]);
            return n != null ? { kind: 'account', lineNumber: n, rawValue: m[2].trim() } : null;
        },
    },

    // ── Line N alone: select active line (no payload required) ──
    {
        regex: /^(?:السطر|سطر|line|row)\s+(\S+)\s*$/i,
        build: (m) => {
            const n = parseSpokenNumber(m[1]);
            return n != null ? { kind: 'line', lineNumber: n, rawValue: '' } : null;
        },
    },
    // ── Line N: payload follows (account + optional desc — amount IGNORED here) ──
    {
        regex: /^(?:السطر|سطر|line|row)\s+(\S+)\s+(.+)$/i,
        build: (m) => {
            const n = parseSpokenNumber(m[1]);
            return n != null ? { kind: 'line', lineNumber: n, rawValue: m[2].trim() } : null;
        },
    },

    // ── Date ──
    {
        regex: /^(?:التاريخ|تاريخ|بتاريخ|في\s+تاريخ|date|on)\s+(.+)$/i,
        build: (m) => ({ kind: 'date', rawValue: m[1].trim() }),
    },

    // ── Voucher reference (V 6334 / رقم السند 6334) ──
    {
        regex: /^(?:رقم\s*السند|السند\s*رقم|رقم\s*المرجع|voucher(?:\s*number|\s*#)?|reference|ref)\s+(.+)$/i,
        build: (m) => ({ kind: 'voucherRef', rawValue: m[1].trim() }),
    },

    // ── Payee ──
    {
        regex: /^(?:المستفيد|مستفيد|اسم\s+المستفيد|payee|paid\s+to|paid)\s+(.+)$/i,
        build: (m) => ({ kind: 'payee', rawValue: m[1].trim() }),
    },

    // ── Notes ──
    {
        regex: /^(?:ملاحظات|ملاحظه|ملاحظة|notes|note)\s+(.+)$/i,
        build: (m) => ({ kind: 'notes', rawValue: m[1].trim() }),
    },
];

/**
 * Interpret a raw spoken transcript and decide whether it is a field-targeted
 * command or free dictation.
 */
export function interpretVoiceCommand(text: string): FieldTarget {
    if (!text || !text.trim()) return { kind: 'free', rawValue: '' };
    const cleaned = text.trim();
    for (const p of KEYWORD_PATTERNS) {
        const m = cleaned.match(p.regex);
        if (m) {
            const built = p.build(m);
            if (built) return built;
        }
    }
    return { kind: 'free', rawValue: cleaned };
}

/**
 * Try parsing common date utterances:
 *   "8/3/2026" / "8-3-2026" / "٨/٣/٢٠٢٦" / "8 مارس 2026" / "8 march 2026"
 * Returns ISO yyyy-mm-dd or null.
 */
const AR_MONTHS: Record<string, number> = {
    'يناير': 1, 'كانون الثاني': 1, 'january': 1, 'jan': 1,
    'فبراير': 2, 'شباط': 2, 'february': 2, 'feb': 2,
    'مارس': 3, 'اذار': 3, 'march': 3, 'mar': 3,
    'ابريل': 4, 'أبريل': 4, 'نيسان': 4, 'april': 4, 'apr': 4,
    'مايو': 5, 'ايار': 5, 'may': 5,
    'يونيو': 6, 'حزيران': 6, 'june': 6, 'jun': 6,
    'يوليو': 7, 'تموز': 7, 'july': 7, 'jul': 7,
    'اغسطس': 8, 'أغسطس': 8, 'اب': 8, 'august': 8, 'aug': 8,
    'سبتمبر': 9, 'ايلول': 9, 'september': 9, 'sep': 9, 'sept': 9,
    'اكتوبر': 10, 'أكتوبر': 10, 'تشرين الاول': 10, 'october': 10, 'oct': 10,
    'نوفمبر': 11, 'تشرين الثاني': 11, 'november': 11, 'nov': 11,
    'ديسمبر': 12, 'كانون الاول': 12, 'december': 12, 'dec': 12,
};

export function parseSpokenDate(input: string): string | null {
    if (!input) return null;
    let t = arabicToLatinDigits(normalizeAr(input)).trim();

    // Pattern: d[-/.]m[-/.]yyyy
    let m = t.match(/(\d{1,2})\s*[-/.]\s*(\d{1,2})\s*[-/.]\s*(\d{2,4})/);
    if (m) {
        const d = parseInt(m[1], 10);
        const mo = parseInt(m[2], 10);
        let y = parseInt(m[3], 10);
        if (y < 100) y += 2000;
        if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31)
            return `${y.toString().padStart(4, '0')}-${mo.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
    }

    // Pattern: "8 مارس 2026" or "march 8 2026"
    for (const [name, num] of Object.entries(AR_MONTHS)) {
        if (t.includes(name)) {
            const rx = new RegExp(`(\\d{1,2})[\\s]*${name}[\\s]*(\\d{2,4})|${name}[\\s]+(\\d{1,2})[\\s,]+(\\d{2,4})`, 'i');
            const mm = t.match(rx);
            if (mm) {
                const d = parseInt(mm[1] || mm[3], 10);
                let y = parseInt(mm[2] || mm[4], 10);
                if (y < 100) y += 2000;
                return `${y}-${num.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
            }
        }
    }

    return null;
}

/** Extract first numeric amount from text. Supports "41.750", "٤١٫٧٥٠", "100 kwd". */
export function parseSpokenAmount(input: string): number | null {
    if (!input) return null;
    const t = arabicToLatinDigits(input).replace(/[٫،]/g, '.').replace(/,/g, '');
    const m = t.match(/(\d+(?:\.\d+)?)/);
    if (!m) return null;
    const v = parseFloat(m[1]);
    return isFinite(v) && v > 0 ? v : null;
}

/** Normalize voucher ref: strip leading "V"/"#"/"رقم" so we can prefix "V " uniformly. */
export function normalizeVoucherRef(input: string): string {
    if (!input) return '';
    const cleaned = arabicToLatinDigits(input)
        .replace(/^\s*(v|#|رقم|number|no\.?)\s*/i, '')
        .replace(/\s+/g, '')
        .trim();
    return cleaned ? `V ${cleaned}` : '';
}
